import { Link } from 'react-router-dom';
import type { MovieItem } from '@/mocks/movies';

interface MovieCardProps {
  movie: MovieItem;
  size?: 'normal' | 'large';
}

function getCategoryLabel(category: string): string {
  const map: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };
  return map[category] || category;
}

export default function MovieCard({ movie, size = 'normal' }: MovieCardProps) {
  const isLarge = size === 'large';

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block cursor-pointer"
    >
      {/* Poster */}
      <div
        className={`relative overflow-hidden rounded-lg mb-3 ${
          isLarge ? 'aspect-[2/3]' : 'aspect-[3/4]'
        }`}
      >
        <img
          src={movie.poster}
          alt={`${movie.title} 海报`}
          title={`${movie.title} - ${movie.titleEn}`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <div className="w-full">
            <p className="text-xs text-foreground-100 line-clamp-2 mb-2">{movie.description}</p>
            <div className="flex items-center justify-center">
              <span className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium">
                <i className="ri-play-fill text-sm"></i>
                立即观看
              </span>
            </div>
          </div>
        </div>

        {/* Rating Badge */}
        <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-background-950/80 backdrop-blur-sm">
          <span className="text-primary-400 text-xs font-bold">{movie.rating}</span>
        </div>

        {/* Update Badge */}
        {movie.isNew && (
          <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-accent-500 text-xs font-medium text-background-50">
            新
          </div>
        )}

        {/* Hot Badge */}
        {movie.isHot && !movie.isNew && (
          <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-primary-500 text-xs font-medium text-background-50">
            热
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        <h3 className="text-sm font-medium text-foreground-50 truncate group-hover:text-primary-400 transition-colors duration-300">
          {movie.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-xs text-foreground-500">{movie.year}</span>
          <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-background-100 text-foreground-400">
            {getCategoryLabel(movie.category)}
          </span>
        </div>
      </div>
    </Link>
  );
}